import type { EventAttachment } from './event-attachments';

type BadgeTone = {
  label: string;
  background: string;
  text: string;
  border: string;
};

const CONTENT_TYPE_TO_CODE: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'application/vnd.ms-powerpoint': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
  'application/zip': 'zip',
  'text/csv': 'csv',
  'text/plain': 'text',
};

function extensionOf(fileName: string | undefined): string | undefined {
  if (!fileName) return undefined;
  const normalized = fileName.toLowerCase();
  const matches = normalized.matchAll(/\.([a-z0-9]{2,5})(?=$|[^a-z0-9])/g);
  let ext: string | undefined;
  for (const match of matches) {
    ext = match[1];
  }
  return ext;
}

function codeFromContentType(contentType: string | undefined): string | undefined {
  const normalized = contentType?.toLowerCase();
  if (!normalized) return undefined;
  if (CONTENT_TYPE_TO_CODE[normalized]) return CONTENT_TYPE_TO_CODE[normalized];
  if (normalized.startsWith('image/')) return 'image';
  if (normalized.startsWith('video/')) return 'video';
  if (normalized.startsWith('audio/')) return 'audio';
  if (normalized.startsWith('text/')) return 'text';
  return undefined;
}

function codeFromExtension(ext: string | undefined): string | undefined {
  if (!ext) return undefined;
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext)) return 'image';
  if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) return 'zip';
  if (['mp3', 'wav', 'm4a', 'aac', 'ogg'].includes(ext)) return 'audio';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video';
  if (['txt', 'md', 'log'].includes(ext)) return 'text';
  if (ext === 'csv') return 'csv';
  return undefined;
}

function toneForAttachment(attachment: EventAttachment): BadgeTone {
  const extension = extensionOf(attachment.fileName);
  const code =
    codeFromExtension(extension) ?? codeFromContentType(attachment.contentType) ?? 'file';

  if (code === 'word') {
    return { label: 'W', background: '#185ABD', text: '#ffffff', border: '#2D6FD3' };
  }
  if (code === 'excel') {
    return { label: 'X', background: '#107C41', text: '#ffffff', border: '#199D56' };
  }
  if (code === 'powerpoint') {
    return { label: 'P', background: '#D24726', text: '#ffffff', border: '#E66747' };
  }
  if (code === 'pdf') {
    return { label: 'PDF', background: '#D92D20', text: '#ffffff', border: '#E54D42' };
  }
  if (code === 'image') {
    return { label: 'IMG', background: '#0284C7', text: '#ffffff', border: '#38BDF8' };
  }
  if (code === 'zip') {
    return { label: 'ZIP', background: '#B45309', text: '#ffffff', border: '#D97706' };
  }
  if (code === 'audio') {
    return { label: 'AUD', background: '#7C3AED', text: '#ffffff', border: '#8B5CF6' };
  }
  if (code === 'video') {
    return { label: 'VID', background: '#BE185D', text: '#ffffff', border: '#DB2777' };
  }
  if (code === 'csv') {
    return { label: 'CSV', background: '#15803D', text: '#ffffff', border: '#22C55E' };
  }
  if (code === 'text') {
    return { label: 'TXT', background: '#475569', text: '#ffffff', border: '#64748B' };
  }
  return { label: 'FIL', background: '#475569', text: '#ffffff', border: '#64748B' };
}

export function AttachmentFileTypeIcon({ attachment }: { attachment: EventAttachment }) {
  const tone = toneForAttachment(attachment);
  const title = attachment.fileName ?? tone.label;

  return (
    <span
      className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-[4px] border px-[1px]"
      title={title}
      aria-hidden="true"
      style={{ background: tone.background, color: tone.text, borderColor: tone.border }}
    >
      <span
        className="leading-none font-semibold"
        style={{ fontSize: tone.label.length > 1 ? '7px' : '10px' }}
      >
        {tone.label}
      </span>
    </span>
  );
}
